(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.QSCalendarEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const DATE = /^\d{4}-\d{2}-\d{2}$/;
  const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
  function validDate(value) {
    if (typeof value !== 'string' || !DATE.test(value)) return false;
    const date = new Date(value + 'T12:00:00Z');
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }
  function safeUrl(value) {
    if (!value || typeof value !== 'string') return '';
    if (/^\/(?!\/)/.test(value)) return value;
    try { const url = new URL(value); return url.protocol === 'https:' ? url.href : ''; }
    catch (_) { return ''; }
  }
  function scheduleFromText(value, anchor='2000-01-01') {
    const text=String(value||'').trim().replace(/[\u2013\u2014]/g,'-');
    if(!text)return null;
    const parts=text.split(';').map(s=>s.trim());
    if(parts.length>2)throw new Error('Use one schedule, with one optional skip list');
    const clock=token=>{
      const m=token.trim().match(/^(\d{1,2})(?::([0-5]\d))?\s*(AM|PM)$/i);
      if(!m||Number(m[1])<1||Number(m[1])>12)throw new Error('Use times such as 3:00 PM');
      return String(Number(m[1])%12+(/pm/i.test(m[3])?12:0)).padStart(2,'0')+':'+(m[2]||'00');
    };
    const range='(\\d{1,2}(?::[0-5]\\d)?\\s*[AP]M)\\s*-\\s*(\\d{1,2}(?::[0-5]\\d)?\\s*[AP]M)';
    const recurring=parts[0].match(new RegExp('^Every (Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|(?:first|second|third|fourth|fifth|last) (?:Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)),\\s*'+range+'(?:,\\s*from (\\d{4}-\\d{2}-\\d{2})(?: until (\\d{4}-\\d{2}-\\d{2}))?)?$','i'));
    let result;
    if(recurring){
      const [,pattern,begin,finish,givenFrom,until]=recurring;
      const from=givenFrom || anchor;
      if(!validDate(from)||(until&&(!validDate(until)||until<from)))throw new Error('Enter valid schedule start and end dates');
      const words=pattern.toLowerCase().split(' '),day=words[words.length-1];
      const days={sunday:'SU',monday:'MO',tuesday:'TU',wednesday:'WE',thursday:'TH',friday:'FR',saturday:'SA'};
      const nth={first:1,second:2,third:3,fourth:4,fifth:5,last:-1};
      result={rrule:words.length===1?'FREQ=WEEKLY;BYDAY='+days[day]:'FREQ=MONTHLY;BYDAY='+nth[words[0]]+days[day],from,start:clock(begin),end:clock(finish),exclude:[]};
      if(until)result.until=until;
    }else{
      const single=parts[0].match(new RegExp('^(\\d{4}-\\d{2}-\\d{2}),\\s*'+range+'$','i'));
      if(!single||!validDate(single[1]))throw new Error('Use the documented Class Date & Time format');
      result={sessions:[{date:single[1],start:clock(single[2]),end:clock(single[3])}]};
    }
    if(parts[1]){
      if(!result.rrule||!/^skip /i.test(parts[1]))throw new Error('Only recurring schedules accept a skip list');
      result.exclude=parts[1].slice(5).split(',').map(s=>s.trim());
      if(result.exclude.some(date=>!validDate(date)))throw new Error('Skip dates must use YYYY-MM-DD');
    }
    const session=result.sessions?.[0]||result;
    if(session.end<=session.start)throw new Error('End time must be after start time');
    return result;
  }
  function expand(sources, from, to, RRule) {
    const events = [], errors = [], ids = new Set();
    const merged = new Map();
    for (const source of sources) merged.set(source.id, {...merged.get(source.id), ...source});
    for (const [key, source] of [...merged]) {
      if (source.enabled === false || !source.scheduleText?.includes('|')) continue;
      try {
        const schedules = source.scheduleText.split('|').map(text => scheduleFromText(text, source.scheduleAnchor || '2000-01-01'));
        if (schedules.some(schedule => !schedule)) throw new Error('Each time slot needs a schedule');
        merged.delete(key);
        schedules.forEach((schedule, index) => merged.set(key + ':slot-' + index, {
          ...source, id: key + ':slot-' + index, groupId: source.id, scheduleText: '', schedule,
          bookings: [...(source.bookings || []), ...(source.schedule?.sessions || [])]
        }));
      } catch (error) {
        merged.delete(key);
        errors.push({source: key, message: error.message});
      }
    }
    for (const source of merged.values()) {
      try {
        if (source.enabled === false || !source.id || !source.title) continue;
        let schedule = typeof source.schedule === 'string' ? JSON.parse(source.schedule) : source.schedule;
        const bookingSessions=[...(source.bookings||[]),...(schedule?.sessions||[])];
        // Rules control visibility; exact dated variants control bookability independently.
        if(/^Every /i.test(source.scheduleText?.trim()||''))schedule=scheduleFromText(source.scheduleText,source.scheduleAnchor||'2000-01-01');
        else if(!schedule?.sessions?.length&&!schedule?.rrule&&source.scheduleText?.trim())schedule=scheduleFromText(source.scheduleText);
        if (!schedule) continue;
        const exclusions = new Set(schedule.exclude || []);
        let sessions = schedule.sessions || [];
        if (schedule.rrule) {
          if (!RRule) throw new Error('Recurrence library unavailable');
          if (!validDate(schedule.from) || (schedule.until&&!validDate(schedule.until))) throw new Error('Recurrence needs valid dates');
          const opts = RRule.parseString(schedule.rrule);
          opts.dtstart = new Date(schedule.from + 'T00:00:00Z');
          if(schedule.until)opts.until = new Date(schedule.until + 'T23:59:59Z');
          const rule = new RRule(opts);
          sessions = rule.between(new Date(from + 'T00:00:00Z'), new Date(to + 'T00:00:00Z'), true)
            .map(date => ({ date: date.toISOString().slice(0, 10), start: schedule.start, end: schedule.end }));
        }
        for (const session of sessions) {
          if (!validDate(session.date) || session.date >= to || exclusions.has(session.date)) continue;
          const endDate = session.endDate || session.date;
          if (!validDate(endDate) || endDate < from) continue;
          const allDay = session.allDay === true;
          if (!allDay && (!TIME.test(session.start) || !TIME.test(session.end))) continue;
          const start = session.date + (allDay ? '' : 'T' + session.start + ':00');
          const end = endDate + (allDay ? '' : 'T' + session.end + ':00');
          if (!allDay && end <= start) continue;
          if (allDay && end < start) continue;
          const variants = source.variants || [];
          const candidates=[...new Map(bookingSessions.filter(s=>s.date===session.date&&s.start===session.start&&s.end===session.end)
            .map(s=>[String(s.variantId||s.variantTitle),s])).values()];
          const booking=session.variantId||session.variantTitle?session:candidates.length===1?candidates[0]:null;
          let variant=booking?variants.find(v=>booking.variantId?String(v.id)===String(booking.variantId):booking.variantTitle&&v.title===booking.variantTitle):null;
          if(!variant&&!schedule.rrule&&variants.length===1&&variants[0].title==='Default Title')variant=variants[0];
          const id = source.id + ':' + session.date + ':' + (session.start || 'day') + (session.variantId ? ':' + session.variantId : '');
          if (ids.has(id)) continue;
          ids.add(id);
          let url = safeUrl(source.url);
          if (variant && url) { const target=new URL(url,'https://www.quiltandsew.com');target.searchParams.set('variant',String(variant.id));url=url.startsWith('/')?target.pathname+target.search+target.hash:target.href; }
          events.push({ id, title: source.title, start, end: allDay ? nextDay(endDate) : end, allDay,
            extendedProps: { category: ['class', 'event', 'sale'].includes(source.category) ? source.category : 'class',
              date: session.date, lastDate: endDate, description: source.description || '', location: source.location || 'QuiltandSew.com, St. George',
              instructor: source.instructor || '', supplies: source.supplies || '', image: safeUrl(typeof source.image==='string'&&source.image.startsWith('//')?'https:'+source.image:source.image), url,
              available: variant ? variant.available : null, matchedVariant: !!variant, price: variant ? variant.price : null,
              currency: source.currency || 'USD', registration: source.registration || 'product',
              bookingState:variant?(variant.available?'bookable':'waitlist'):'request',
              variantId: variant ? String(variant.id) : null, sourceId: source.id, groupId: source.groupId || source.id } });
        }
      } catch (error) { errors.push({ source: source.id, message: error.message }); }
    }
    return { events: events.sort((a,b) => a.start.localeCompare(b.start) || a.title.localeCompare(b.title)), errors };
  }
  function nextDay(date) { const d = new Date(date + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate()+1); return d.toISOString().slice(0,10); }
  function ics(event) {
    const p = event.extendedProps;
    const escape = value => String(value || '').replace(/\\/g,'\\\\').replace(/\r?\n/g,'\\n').replace(/,/g,'\\,').replace(/;/g,'\\;');
    const lines = ['BEGIN:VCALENDAR','VERSION:2.0','PRODID:-//QuiltandSew//Classes Calendar//EN',
      'BEGIN:VTIMEZONE','TZID:America/Denver','BEGIN:DAYLIGHT','DTSTART:20070311T020000','TZOFFSETFROM:-0700','TZOFFSETTO:-0600','RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU','END:DAYLIGHT',
      'BEGIN:STANDARD','DTSTART:20071104T020000','TZOFFSETFROM:-0600','TZOFFSETTO:-0700','RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU','END:STANDARD','END:VTIMEZONE','BEGIN:VEVENT',
      'UID:' + escape(event.id) + '@quiltandsew.com','DTSTAMP:' + new Date().toISOString().replace(/[-:]/g,'').replace(/\.\d{3}/,''),
      event.allDay ? 'DTSTART;VALUE=DATE:' + event.start.replace(/-/g,'') : 'DTSTART;TZID=America/Denver:' + event.start.replace(/[-:]/g,''),
      event.allDay ? 'DTEND;VALUE=DATE:' + event.end.replace(/-/g,'') : 'DTEND;TZID=America/Denver:' + event.end.replace(/[-:]/g,''),
      'SUMMARY:' + escape(event.title),'LOCATION:' + escape(p.location),'DESCRIPTION:' + escape(p.description),
      'END:VEVENT','END:VCALENDAR'];
    // Fold on UTF-8 byte boundaries as required by RFC 5545.
    return lines.map(line => { let result='', count=0; for (const c of line) { const n = new TextEncoder().encode(c).length;
      if (count+n>73) { result+='\r\n '; count=1; } result+=c; count+=n; } return result; }).join('\r\n')+'\r\n';
  }
  return { expand, validDate, safeUrl, nextDay, ics, scheduleFromText };
});
